import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { Observable, ReplaySubject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { Order, OrderListItem, orderStatusTypeLookup, CANCEL_ON_NAVIGATION } from 'reg-hub-common';
import { Constants } from 'projects/reg-hub-admin/src/constants';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';

const START_RETRY_DELAY_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class OrdersHubService implements OnDestroy {
  private client: WebPubSubClient | undefined;
  private messagesSubject = new ReplaySubject<any>(1);  // stores last message, can be Subject if you prefer
  public messages$: Observable<any> = this.messagesSubject.asObservable();

  private manualOrder = 'ManualOrder';
  private failedOrder = 'FailedOrder';
  private handledOrder = 'FailedOrManualOrderHandled';
  private pendingAuditCount = 'PendingAuditCount';

  private joinedGroups = new Set<string>();

  // BehaviorSubjects to store orders
  private manualOrdersSource = new BehaviorSubject<OrderListItem[]>([]);
  manualOrders$ = this.manualOrdersSource.asObservable();

  private manualOrdersCount = new BehaviorSubject<number>(0);
  manualOrdersCount$ = this.manualOrdersCount.asObservable();

  private failedOrdersSource = new BehaviorSubject<OrderListItem[]>([]);
  failedOrders$ = this.failedOrdersSource.asObservable();

  private failedOrdersCount = new BehaviorSubject<number>(0);
  failedOrdersCount$ = this.failedOrdersCount.asObservable();

  private pendingAuditCountSource = new BehaviorSubject<number>(0);
  pendingAuditCount$ = this.pendingAuditCountSource.asObservable();

  constructor(private http: HttpClient,
    private environmentUrl: EnvironmentUrlService
  ) {}

  connect(): void {
    // The client is created synchronously so a second connect(), even in the same tick, is a no-op
    // rather than a second negotiate and an orphaned connection
    if (this.client) {
      return;
    }

    this.client = new WebPubSubClient({
      // Negotiate on every (re)connect since access URLs expire
      getClientAccessUrl: async () => {
        const response = await firstValueFrom(
          this.http.get<{ url: string }>(`${this.environmentUrl.urlAddress}${Constants.ordersHubNegotiateUrl}`, { context: new HttpContext().set(CANCEL_ON_NAVIGATION, false) }));
        return response.url;
      }
    }, {
      // Each reconnect attempt negotiates with the API, so back off instead of retrying every second
      reconnectRetryOptions: { mode: "Exponential" }
    });

    // Set up event handlers
    this.client.on("connected", (e) => {
      console.log("Connected to Orders hub:", e.connectionId);
    });

    // handle messages to a group when received
    this.client.on("group-message", (messageEvent) => {
      this.handleGroupMessageReceived(messageEvent);
    });

    // handle disconnection from the hub
    this.client.on("disconnected", () => {
      console.warn("Disconnected from Web PubSub");
    });

    this.start();
  }

  // Once started, the client reconnects by itself, but a failed start (e.g. the negotiate fails) is never retried
  private start() {
    this.client?.start()
      .then(() => this.joinDashboardGroups())
      .catch(err => {
        console.error('Error starting client', err);
        setTimeout(() => this.start(), START_RETRY_DELAY_MS);
      });
  }

  private joinGroupIfNotJoined(groupName: string) {
    if (!this.joinedGroups.has(groupName)) {
      this.client?.joinGroup(groupName)
        .then(() => {
          this.joinedGroups.add(groupName);
        })
    }
  }

  private leaveGroupIfJoined(groupName: string) {
    if (this.joinedGroups.has(groupName)) {
      this.client?.leaveGroup(groupName)
        .then(() => {
          this.joinedGroups.delete(groupName);
        })
    }
  }

  joinDashboardGroups() {
    this.joinGroupIfNotJoined(this.failedOrder);
    this.joinGroupIfNotJoined(this.manualOrder);
    this.joinGroupIfNotJoined(this.handledOrder);
  }

  leaveDashboardGroups() {
    this.leaveGroupIfJoined(this.failedOrder);
    this.leaveGroupIfJoined(this.manualOrder);
    this.leaveGroupIfJoined(this.handledOrder);
  }

  joinPendingAuditsGroup() {
    this.joinGroupIfNotJoined(this.pendingAuditCount);
  }

  leavePendingAuditsGroup() {
    this.leaveGroupIfJoined(this.pendingAuditCount);
  }

  handleGroupMessageReceived(messageEvent: OnGroupDataMessageArgs) {
    if (messageEvent.message.dataType === "text") {
      const group: string = messageEvent.message.group;
      const rawOrder = JSON.parse(messageEvent.message.data as string, this.toCamelCase);
      const order = this.toOrderListItem(rawOrder);
      console.log("Received order message:", order);

      switch (group) {
        case this.failedOrder:
          this.addOrUpdateFailedOrder(order);
          break;
        case this.manualOrder:
          this.addOrUpdateManualOrder(order);
          break;
        case this.handledOrder:
          this.removeOrder(order.id);
          break;
        default:
          // This shouldnt happen, we only subscribed to the three groups here? Maybe for Orders?
          console.warn(`Received a message from an unhandled group: ${group}`, order);
          break;
      }

      this.messagesSubject.next(order);
    }
  }

  private toOrderListItem(raw: any): OrderListItem {
    return {
      id: raw.id,
      referenceNumber: raw.referenceNumber,
      orderTypeID: raw.orderTypeID,
      orderStatusTypeID: raw.orderStatusTypeID,
      jurisdiction: raw.jurisdiction,
      displayJurisdiction: raw.displayJurisdiction,
      country: raw.country,
      dateRequested: raw.dateRequested,
      orderStatusUpdatedDate: raw.orderStatusUpdatedDate,
      isVisible: raw.isVisible,
      orderTypeName: raw.orderType?.name ?? raw.orderTypeName ?? '',
      description: raw.description ?? '',
      hasUnreadCustomerMessages: raw.hasUnreadCustomerMessages ?? null,
      hasUnreadAdminMessages: raw.hasUnreadAdminMessages ?? null,
      priority: raw.priority ?? 0,
      added: raw.added ? new Date(raw.added) : new Date()
    };
  }

  public setInitialManualOrders(orders: OrderListItem[], count: number) {
    this.manualOrdersSource.next(orders);
    this.manualOrdersCount.next(count);
  }

  public setInitialFailedOrders(orders: OrderListItem[], count: number) {
    this.failedOrdersSource.next(orders);
    this.failedOrdersCount.next(count);
  }

  private addOrUpdateManualOrder(order: OrderListItem) {
    let orders = this.manualOrdersSource.value;
    let count = this.manualOrdersCount.value;
    const index = orders.findIndex((o) => o.id === order.id);

    order.orderStatusTypeID = orderStatusTypeLookup[Number(order.orderStatusTypeID)] ?? order.orderStatusTypeID;

    if (index !== -1) {
      // Update existing order
      orders[index] = order;
    } else {
      // Add new order
      orders.unshift(order);
      count = count+1;
      // Keep only the top 10 orders
      if (orders.length > 10) {
        orders = orders.slice(0, 10);
      }
    }

    this.manualOrdersSource.next([...orders]);
    this.manualOrdersCount.next(count);
  }

  private addOrUpdateFailedOrder(order: OrderListItem) {
    let orders = this.failedOrdersSource.value;
    let count = this.failedOrdersCount.value;
    const index = orders.findIndex((o) => o.id === order.id);

    order.orderStatusTypeID = orderStatusTypeLookup[Number(order.orderStatusTypeID)] ?? order.orderStatusTypeID;

    if (index !== -1) {
      // Update existing order
      orders[index] = order;
    } else {
      // Add new order
      orders.unshift(order);
      count = count+1;
      // Keep only the top 10 orders
      if (orders.length > 10) {
        orders = orders.slice(0, 10);
      }
    }

    this.failedOrdersSource.next([...orders]);
    this.failedOrdersCount.next(count);
  }

  private removeOrder(orderId: string) {
    // Remove from manual orders
    if (this.manualOrdersSource.value.find(o => o.id === orderId)) {
      let manualOrders = this.manualOrdersSource.value.filter((o) => o.id !== orderId);
      this.manualOrdersSource.next([...manualOrders]);
      this.manualOrdersCount.next(this.manualOrdersCount.value-1);
    }

    // Remove from failed orders
    if (this.failedOrdersSource.value.find(o => o.id === orderId)) {
      let failedOrders = this.failedOrdersSource.value.filter((o) => o.id !== orderId);
      this.failedOrdersSource.next([...failedOrders]);
      this.failedOrdersCount.next(this.failedOrdersCount.value-1);
    }
  }

  ngOnDestroy() {
    // Stop the connection if the service is destroyed (e.g., in certain testing scenarios)
    this.client?.stop();
    this.client = undefined;
  }

  toCamelCase(key: any, value: any) {
    if (value && typeof value === 'object'){
      for (var k in value) {
        // Special case: If the key is exactly 'ID', rename it to 'id'
        if (k === 'ID') {
          value['id'] = value[k];
          delete value[k];
        }
        // Otherwise, just lowercase the first letter
        else if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
          value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
          delete value[k];
        }
      }
    }
    return value;
  }
}